/**
 * Admin migration PR-1 — useAdminGate hook.
 *
 * The single source of truth for the `/v2/admin` route guard. Resolves
 * the signed-in user's admin status + permission map once via
 * `GET /api/admin/check`, then caches it per user id so switching admin
 * tabs (same `<Admin>` element, different `:tab` param) never re-fetches.
 *
 * State machine (mirrors AuthContext's non-gating philosophy — the hook
 * exposes state, the route decides what to render):
 *   - auth still loading                 → { loading: true }
 *   - no Supabase session                → { signedIn: false }      (route → /landing)
 *   - signed in, /check resolves         → { isAdmin, permissions }  (route gates on these)
 *   - signed in, /check throws           → { error }                (route shows an error panel,
 *                                           NOT a bounce to /today)
 *
 * Permission keys mirror the `admins.permissions` JSON in the DB
 * (`api/index.js` verifyAdmin). Tabs backed by handlers that only check
 * `isAdmin` (analytics/audit/models/bugs/promo) carry `permission: null`
 * in the registry and don't consult this map.
 */

import { useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAuthFetch } from './useAuthFetch';

export type AdminPermissionKey =
  | 'users'
  | 'admins'
  | 'prompts'
  | 'flags'
  | 'analytics'
  | 'knowledge';

export type AdminPermissions = Partial<Record<AdminPermissionKey, boolean>>;

export interface AdminGateState {
  /** True until auth resolves AND (when signed in) /api/admin/check returns. */
  loading: boolean;
  /** False when there is no Supabase session. Route → /landing. */
  signedIn: boolean;
  /** True only when the signed-in user has an `admins` row. */
  isAdmin: boolean;
  /** Permission map from that row ({} when not an admin or on error). */
  permissions: AdminPermissions;
  /** Non-null when the /api/admin/check fetch itself failed. */
  error: Error | null;
}

interface CheckResult {
  isAdmin: boolean;
  permissions: AdminPermissions;
  /** B88 — when this answer was fetched; a NEGATIVE expires after NEGATIVE_TTL_MS. */
  at: number;
}

// Per-user-id caches. `cache` holds resolved results so re-mounts /
// multiple consumers don't re-hit the endpoint; `inflight` dedupes a
// concurrent first fetch. Exported reset is for tests only.
// B88 — a negative answer is remembered only briefly. A positive is stable for
// a session; a false could be a real "not an admin" or the tail of a server
// hiccup (the server now reports a hiccup as a 503, which is never cached), so
// even a genuine false is re-asked after a few minutes rather than for the
// whole tab.
export const NEGATIVE_TTL_MS = 5 * 60 * 1000;
const cache = new Map<string, CheckResult>();
const inflight = new Map<string, Promise<CheckResult>>();

/** Test-only — clear the module-level admin-check cache. */
export function __resetAdminGateCache(): void {
  cache.clear();
  inflight.clear();
}

const INITIAL: AdminGateState = {
  loading: true,
  signedIn: false,
  isAdmin: false,
  permissions: {},
  error: null,
};

export function useAdminGate(): AdminGateState {
  const { loading: authLoading, session, user } = useAuthContext();
  const authFetch = useAuthFetch();
  const [state, setState] = useState<AdminGateState>(INITIAL);

  useEffect(() => {
    // Hold in the loading state until auth settles.
    if (authLoading) {
      setState((s) => (s.loading ? s : INITIAL));
      return;
    }

    // Resolved + signed out → terminal "not signed in" state.
    if (!session || !user) {
      setState({
        loading: false,
        signedIn: false,
        isAdmin: false,
        permissions: {},
        error: null,
      });
      return;
    }

    const uid = user.id;
    let cancelled = false;

    const cachedRaw = cache.get(uid);
    const cached =
      cachedRaw && (cachedRaw.isAdmin || Date.now() - cachedRaw.at < NEGATIVE_TTL_MS) ? cachedRaw : undefined;
    if (cachedRaw && !cached) cache.delete(uid);
    if (cached) {
      setState({
        loading: false,
        signedIn: true,
        isAdmin: cached.isAdmin,
        permissions: cached.permissions,
        error: null,
      });
      return;
    }

    setState((s) => ({ ...s, loading: true, signedIn: true, error: null }));

    const fetchCheck =
      inflight.get(uid) ??
      (async (): Promise<CheckResult> => {
        const res = await authFetch('/api/admin/check');
        if (!res.ok) {
          throw new Error(`admin check failed (${res.status})`);
        }
        const json = (await res.json()) as {
          isAdmin?: unknown;
          permissions?: unknown;
        };
        const result: CheckResult = {
          isAdmin: json.isAdmin === true,
          permissions:
            json.permissions && typeof json.permissions === 'object'
              ? (json.permissions as AdminPermissions)
              : {},
          at: Date.now(),
        };
        cache.set(uid, result);
        return result;
      })();
    inflight.set(uid, fetchCheck);

    fetchCheck
      .then((result) => {
        inflight.delete(uid);
        if (cancelled) return;
        setState({
          loading: false,
          signedIn: true,
          isAdmin: result.isAdmin,
          permissions: result.permissions,
          error: null,
        });
      })
      .catch((err: unknown) => {
        inflight.delete(uid);
        if (cancelled) return;
        setState({
          loading: false,
          signedIn: true,
          isAdmin: false,
          permissions: {},
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, session, user, authFetch]);

  return state;
}
